import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";

const initialState = {
  isLoading: false,
  error: false,
  invoice: null,
  invoices: [],
  dataSessions: [],
  pagedResult: null,
  subscriptionInvoice: [],
  subscriptionInvoicePaged: null,
};

// Tras la migración Outbox: los endpoints de mutación devuelven 200 vacío y la
// generación real ocurre en TaskMicro. NO aplicamos optimistic update porque
// el resultado puede incluir filas nuevas (p.ej. al "eliminar" una factura el
// handler crea una rectificativa que reemplaza a la original) — sería
// confuso ocultar la fila y luego reaparecer cambiada. Solo refrescamos.
const REFETCH_DELAY_MS = 1000;

// ----------------------------------------------------------------------

const slice = createSlice({
  name: "invoices",
  initialState,
  reducers: {
    // LOADING
    startLoading(state) {
      state.isLoading = true;
    },
    stopLoading(state) {
      state.isLoading = false;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    getInvoicesSuccess(state, action) {
      state.invoices = action.payload.invoices;
    },

    getDataSessionsSuccess(state, action) {
      state.dataSessions = action.payload.items;
      state.pagedResult = action.payload;
    },

    getSubscriptionInvoiceSuccess(state, action) {
      state.subscriptionInvoice = action.payload;
    },

    getSubscriptionInvoicePagedSuccess(state, action) {
      state.subscriptionInvoice = action.payload.items;
      state.subscriptionInvoicePaged = action.payload;
    },

    deleteInvoiceSuccess(state, action) {
      state.invoices = state.invoices.filter((u) => u.id !== action.payload);
    },

    deleteDataSessionsSuccess(state, action) {
      state.dataSessions = state.dataSessions.filter(
        (u) => u.idSession !== action.payload,
      );
    },

    createInvoiceSuccess(state, action) {
      state.invoices = [...action.payload, ...state.invoices];
    },

    // REDUCERS...
  },
});

// ----------------------------------------------------------------------

// Reducer
export default slice.reducer;

// Actions
export const {
  getInvoicesSuccess,
  deleteInvoiceSuccess,
  deleteDataSessionsSuccess,
} = slice.actions;

// ----------------------------------------------------------------------

export function GetInvoices() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const userId = window.localStorage.getItem("userId");
      var response = await axios.get(`invoice/get/${userId}`);
      dispatch(slice.actions.getInvoicesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function GetSubscriptionInvoice() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const userId = window.localStorage.getItem("userId");
      var response = await axios.get(`invoice/subscription/${userId}`);
      dispatch(slice.actions.getSubscriptionInvoiceSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

/**
 * El backend encola la eliminación en Outbox y responde 200 vacío. El handler
 * genera una rectificativa, así que la fila original sigue apareciendo (con
 * la rectificativa adjunta) — no usamos optimistic delete; solo refrescamos.
 *
 * @param {number} invoiceId  Id de la factura a eliminar.
 * @param {Function} [refetch] Opcional. Si se provee, se llama a los REFETCH_DELAY_MS;
 *                              en su defecto se llama a GetInvoices().
 */
export function DeleteInvoices(invoiceId, refetch) {
  return async (dispatch) => {
    try {
      await axios.delete(`invoice/delete/${invoiceId}`);
      setTimeout(() => {
        if (typeof refetch === "function") refetch();
        else dispatch(GetInvoices());
      }, REFETCH_DELAY_MS);
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function SendInvoices(invoice, refetch) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      await axios.post(`invoice/send`, { ...invoice });
      // No hay optimistic — el send no muta la lista visible. Solo refrescamos
      // por si el backend marca la factura como "enviada" tras el envío.
      setTimeout(() => {
        if (typeof refetch === "function") refetch();
        else dispatch(GetInvoices());
      }, REFETCH_DELAY_MS);
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function CreateInvoiceFromTicket(data) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      var invoice = await axios.post(`ticket/CreateInvoiceFromTicket`, data);
      dispatch(slice.actions.createInvoiceSuccess(invoice.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function GetPaginatedSessions(data) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      var response = await axios.post(`data/get`, data);
      dispatch(slice.actions.getDataSessionsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function GetPaginatedInvoices(data) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      var response = await axios.post(`invoice/GetInvoicesPaged`, data);
      dispatch(slice.actions.getSubscriptionInvoicePagedSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

/**
 * Eliminación de suscripción. El handler corre async en TaskMicro y la fila
 * permanece visible (cambia su estado/rectificativa) — refetch directo.
 *
 * @param {number} id        Id de la suscripción.
 * @param {Function} [refetch] Recarga de la lista paginada con el query actual.
 */
export function DeleteSuscriptionInvoice(id, refetch) {
  return async (dispatch) => {
    try {
      await axios.delete(`invoice/DeleteSubscriptionInvoice/${id}`);
      setTimeout(() => {
        if (typeof refetch === "function") refetch();
      }, REFETCH_DELAY_MS);
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

/**
 * Crear factura rectificativa. Devuelve 200 vacío; el handler la genera en
 * TaskMicro. Refetch retrasado para que el caller vea la rectificativa nueva
 * en la tabla.
 */
export function CreateCorrectiveInvoice(data, refetch) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.post(`invoice/CreateCorrective`, data);
      setTimeout(() => {
        if (typeof refetch === "function") refetch();
      }, REFETCH_DELAY_MS);
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      if (typeof refetch === "function") refetch();
    }
  };
}

// ── Override secuencia (admin) ────────────────────────────────────────
// Leer y modificar el número de la próxima factura para un (rest, tipo, año).
// Útil para continuar numeración heredada o corregir huecos. No usan
// dispatch porque son acciones puntuales del panel admin.

export async function fetchInvoiceSequence({ idRestaurante, year, type = 0 }) {
  const params = new URLSearchParams();
  if (idRestaurante) params.set("idRestaurante", idRestaurante);
  if (year) params.set("year", year);
  params.set("type", String(type));
  const res = await axios.get(`invoice/GetSequence?${params.toString()}`);
  return res.data;
}

export async function setInvoiceSequence({ idRestaurante, year, type = 0, nextNumber }) {
  await axios.post(`invoice/SetSequence`, {
    idRestaurante: idRestaurante || "00000000-0000-0000-0000-000000000000",
    type,
    year,
    nextNumber,
  });
}

export async function clearInvoiceSequence({ idRestaurante, year, type = 0 }) {
  const params = new URLSearchParams();
  if (idRestaurante) params.set("idRestaurante", idRestaurante);
  params.set("year", year);
  params.set("type", String(type));
  await axios.delete(`invoice/ClearSequence?${params.toString()}`);
}

/**
 * Crea una factura manual desde el panel admin. Devuelve 200 vacío —
 * el handler real corre en TaskMicro vía Outbox (CreateCustomInvoice).
 * El refresh de la lista lo hace el caller tras un pequeño delay.
 */
export async function createCustomInvoice(payload) {
  // payload shape (camelCase):
  // {
  //   data: ISO string fecha,
  //   type: 0=FACTURA | 1=SIMPLIFICADA | 2=CORRECTIVA,
  //   userId: Guid? cliente destinatario,
  //   products: [{ name, units, description, priceByUnit, total, iva }],
  //   origen: { companyName, realName, companyId, address, localidad, provincia, cp, email },
  //   destination: { companyName, realName, companyId, address, localidad, provincia, cp, email }
  // }
  await axios.post("invoice/CreateCustomInvoice", payload);
}
